import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuth } from '../../../contexts/AuthContext'
import { mapCampaign, countFolderEmails, filterMailboxEmails } from './adminToolShared'
import EmailComposeModal, { buildReplyDraft, buildForwardDraft, buildComposeDraft } from './EmailComposeModal'
import { toastEmailCodeResult } from '../../../utils/mailFeedback'
import { RsIcon, resolveIconNode } from '../../../components/icons'

const TABS = [
  { id: 'inbox', label: 'Mailbox' },
  { id: 'super', label: 'Super Email' },
  { id: 'settings', label: 'Mailbox Settings' },
]

const FOLDERS = [
  { id: 'inbox', icon: 'inbox', name: 'Inbox', countKey: 'inbox' },
  { id: 'starred', icon: 'star', name: 'Starred' },
  { id: 'sent', icon: 'email', name: 'Sent' },
  { id: 'drafts', icon: 'fileText', name: 'Drafts', countKey: 'drafts' },
  { id: 'archive', icon: 'archive', name: 'Archive' },
  { id: 'trash', icon: 'trash', name: 'Trash' },
]

const FOLDER_LABELS = Object.fromEntries(FOLDERS.map((f) => [f.id, f.name]))

const CAMPAIGN_STATUS = {
  draft: { label: 'Draft', cls: 'draft' },
  pending: { label: 'Pending', cls: 'sending' },
  approved: { label: 'Approved', cls: 'scheduled' },
  sent: { label: 'Sent', cls: 'sent' },
  rejected: { label: 'Rejected', cls: 'draft' },
}

const MAILBOX_ACCOUNT_DEFS = [
  { id: 'gmail', type: 'gmail', label: 'Gmail', provider: 'googleCalendar' },
  { id: 'outlook', type: 'outlook', label: 'Microsoft Outlook', provider: 'outlook' },
  { id: 'exchange', type: 'exchange', label: 'Exchange', provider: null },
]

const LIST_FILTERS = [
  { id: 'all', label: 'All messages' },
  { id: 'unread', label: 'Unread only' },
  { id: 'starred', label: 'Starred' },
  { id: 'attachments', label: 'With attachments' },
]

const EXTRACTION_PREFS_KEY = 'rs-mailbox-extraction-prefs'

const DEFAULT_EXTRACTION_PREFS = {
  dealRefs: true,
  meetings: true,
  confidential: true,
}

const AUDIENCE_OPTIONS = [
  { value: 'lp', label: 'All LP' },
  { value: 'gp', label: 'All GP' },
  { value: 'all', label: 'All Users' },
]

export default function AdminEmailView() {
  const { isAdmin, user } = useAuth()
  const userEmail = user?.work_email || 'admin@rotationstreet.com'
  const [mailboxAccounts, setMailboxAccounts] = useState([])
  const [tab, setTab] = useState('inbox')
  const [emails, setEmails] = useState([])
  const [selectedEmail, setSelectedEmail] = useState(null)
  const [folder, setFolder] = useState('inbox')
  const [accountFilter, setAccountFilter] = useState(null)
  const [campaigns, setCampaigns] = useState([])
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [search, setSearch] = useState('')
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [loadingMailbox, setLoadingMailbox] = useState(false)
  const [connecting, setConnecting] = useState(null)
  const [listFilter, setListFilter] = useState('all')
  const [showFilterMenu, setShowFilterMenu] = useState(false)
  const [showAddAccount, setShowAddAccount] = useState(false)
  const [newAccountEmail, setNewAccountEmail] = useState('')
  const [newAccountLabel, setNewAccountLabel] = useState('')
  const [addingAccount, setAddingAccount] = useState(false)
  const [verifyTarget, setVerifyTarget] = useState(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [verifySending, setVerifySending] = useState(false)
  const [verifySubmitting, setVerifySubmitting] = useState(false)
  const [credentialsTarget, setCredentialsTarget] = useState(null)
  const [appPassword, setAppPassword] = useState('')
  const [savingCredentials, setSavingCredentials] = useState(false)
  const [syncingAccount, setSyncingAccount] = useState(null)
  const [showCampaignModal, setShowCampaignModal] = useState(false)
  const [campaignForm, setCampaignForm] = useState({ name: '', subject: '', body: '', audience: 'lp' })
  const [creatingCampaign, setCreatingCampaign] = useState(false)
  const [extractionPrefs, setExtractionPrefs] = useState(() => {
    try {
      return { ...DEFAULT_EXTRACTION_PREFS, ...JSON.parse(localStorage.getItem(EXTRACTION_PREFS_KEY) || '{}') }
    } catch {
      return DEFAULT_EXTRACTION_PREFS
    }
  })
  const [composeOpen, setComposeOpen] = useState(false)
  const [composeMode, setComposeMode] = useState('compose')
  const [composeInitial, setComposeInitial] = useState({})
  const [sendingMail, setSendingMail] = useState(false)

  const loadAccounts = useCallback(async () => {
    try {
      const res = await axios.get('/api/mailbox/accounts')
      setMailboxAccounts(res.data?.length ? res.data : MAILBOX_ACCOUNT_DEFS.map((acc) => ({ ...acc, email: userEmail, connected: false })))
    } catch {
      setMailboxAccounts(MAILBOX_ACCOUNT_DEFS.map((acc) => ({ ...acc, email: userEmail, connected: false })))
    }
  }, [userEmail])

  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true)
    try {
      const url = isAdmin ? '/api/admin/email-campaigns' : '/api/email-campaigns'
      const res = await axios.get(url)
      const mapped = res.data.map(mapCampaign)
      setCampaigns(mapped)
      setSelectedCampaign((prev) => {
        if (prev) return mapped.find((c) => c.id === prev.id) || mapped[0] || null
        return mapped[0] || null
      })
    } catch {
      toast.error('Failed to load campaigns')
    } finally {
      setLoadingCampaigns(false)
    }
  }, [isAdmin])

  const loadMailbox = useCallback(async () => {
    setLoadingMailbox(true)
    try {
      const res = await axios.get('/api/mailbox', { params: { search: search || undefined } })
      setEmails(res.data || [])
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load mailbox')
      setEmails([])
    } finally {
      setLoadingMailbox(false)
    }
  }, [search])

  useEffect(() => {
    loadAccounts()
  }, [loadAccounts])

  useEffect(() => {
    if (tab === 'inbox') loadMailbox()
  }, [tab, loadMailbox])

  useEffect(() => {
    if (tab === 'super') loadCampaigns()
  }, [tab, loadCampaigns])

  useEffect(() => {
    if (tab === 'settings') loadAccounts()
  }, [tab, loadAccounts])

  const filteredEmails = useMemo(
    () => filterMailboxEmails(emails, { folder, accountId: accountFilter, search, listFilter }),
    [emails, folder, accountFilter, search, listFilter]
  )

  const activeAccount = useMemo(
    () => mailboxAccounts.find((a) => a.id === accountFilter) || null,
    [mailboxAccounts, accountFilter]
  )

  const inboxUnread = useMemo(() => countFolderEmails(emails, 'inbox'), [emails])
  const draftsCount = useMemo(() => countFolderEmails(emails, 'drafts'), [emails])

  const selectVisibleEmails = (folderId, accountId = null) => {
    return filterMailboxEmails(emails, { folder: folderId, accountId, search })
  }

  const handleFolderChange = (folderId) => {
    setAccountFilter(null)
    setFolder(folderId)
    setSelectedEmail(selectVisibleEmails(folderId, null)[0] || null)
  }

  const handleAccountSelect = (accountId) => {
    setAccountFilter(accountId)
    setFolder('inbox')
    setSelectedEmail(selectVisibleEmails('inbox', accountId)[0] || null)
  }

  useEffect(() => {
    const visible = filterMailboxEmails(emails, { folder, accountId: accountFilter, search })
    setSelectedEmail((prev) => {
      if (prev && visible.some((e) => e.id === prev.id)) return prev
      return visible[0] || null
    })
  }, [search, emails, folder, accountFilter])

  const listTitle = activeAccount
    ? `${FOLDER_LABELS[folder] || 'Inbox'} · ${activeAccount.email}`
    : (FOLDER_LABELS[folder] || 'Inbox')

  const listSubtitle = activeAccount ? activeAccount.label : 'All mailboxes'

  const handleSelectEmail = (email) => {
    const next = email.unread ? { ...email, unread: false } : email
    setSelectedEmail(next)
    if (email.unread) {
      setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, unread: false } : e)))
      axios.patch(`/api/mailbox/${email.id}`, { unread: false }).catch(() => {})
    }
  }

  const openCompose = (mode, initial = {}) => {
    setComposeMode(mode)
    setComposeInitial(initial)
    setComposeOpen(true)
  }

  const handleComposeSend = async (payload) => {
    setSendingMail(true)
    try {
      if (payload.draftId) {
        await axios.post(`/api/mailbox/${payload.draftId}/send`, payload)
      } else {
        await axios.post('/api/mailbox', { ...payload, send: true })
      }
      toast.success('Email sent')
      setComposeOpen(false)
      await loadMailbox()
      setFolder('sent')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send email')
    } finally {
      setSendingMail(false)
    }
  }

  const handleSaveDraft = async (payload) => {
    setSendingMail(true)
    try {
      if (payload.draftId) {
        await axios.patch(`/api/mailbox/${payload.draftId}`, payload)
      } else {
        await axios.post('/api/mailbox', { ...payload, saveDraft: true })
      }
      toast.success('Draft saved')
      setComposeOpen(false)
      await loadMailbox()
      setFolder('drafts')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save draft')
    } finally {
      setSendingMail(false)
    }
  }

  const handleArchive = async () => {
    if (!selectedEmail) return
    try {
      const res = await axios.post(`/api/mailbox/${selectedEmail.id}/archive`)
      toast.success('Archived')
      setEmails((prev) => prev.map((e) => (e.id === selectedEmail.id ? res.data.item : e)))
      setSelectedEmail(res.data.item)
      if (folder === 'inbox') setSelectedEmail(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to archive')
    }
  }

  const handleTrash = async () => {
    if (!selectedEmail) return
    try {
      const res = await axios.post(`/api/mailbox/${selectedEmail.id}/trash`)
      toast.success('Moved to trash')
      setEmails((prev) => prev.filter((e) => e.id !== selectedEmail.id))
      setSelectedEmail(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete')
    }
  }

  const toggleStar = async (email, e) => {
    e?.stopPropagation?.()
    const starred = !email.starred
    setEmails((prev) => prev.map((item) => (item.id === email.id ? { ...item, starred } : item)))
    if (selectedEmail?.id === email.id) setSelectedEmail((prev) => ({ ...prev, starred }))
    try {
      await axios.patch(`/api/mailbox/${email.id}`, { starred })
    } catch {
      toast.error('Failed to update star')
    }
  }

  const handleApprove = async (id) => {
    try {
      await axios.post(`/api/admin/email-approval/${id}`, { decision: 'approved' })
      toast.success('Campaign approved and sent')
      await loadCampaigns()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approval failed')
    }
  }

  const handleReject = async (id) => {
    try {
      await axios.post(`/api/admin/email-approval/${id}`, { decision: 'rejected', adminNotes: 'Rejected by admin' })
      toast.success('Campaign rejected')
      await loadCampaigns()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reject failed')
    }
  }

  const refresh = () => {
    if (tab === 'super') loadCampaigns()
    if (tab === 'inbox') loadMailbox()
  }

  const isConnected = (acc) => !!acc.connected

  const handleConnect = async (acc) => {
    if (!acc.provider) {
      openVerifyModal(acc)
      return
    }
    setConnecting(acc.id)
    try {
      const res = await axios.post(`/api/mailbox/accounts/${acc.id}/connect`)
      if (res.data.url) {
        window.location.href = res.data.url
        return
      }
      toast.success(`${acc.label} connected`)
      await loadAccounts()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Connect failed')
    } finally {
      setConnecting(null)
    }
  }

  const openVerifyModal = (acc) => {
    setVerifyTarget(acc)
    setVerifyCode('')
  }

  const handleSendVerifyCode = async (acc = verifyTarget) => {
    if (!acc) return
    setVerifySending(true)
    try {
      const res = await axios.post(`/api/mailbox/accounts/${acc.id}/send-verify-code`)
      toastEmailCodeResult(res, toast, `Verification code sent to ${acc.email}`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send verification code')
    } finally {
      setVerifySending(false)
    }
  }

  const handleVerifySubmit = async (e) => {
    e.preventDefault()
    if (!verifyTarget) return
    setVerifySubmitting(true)
    try {
      const res = await axios.post(`/api/mailbox/accounts/${verifyTarget.id}/verify`, { code: verifyCode })
      toast.success(res.data.message || 'Email verified')
      setVerifyTarget(null)
      setVerifyCode('')
      await loadAccounts()
      const verified = res.data.account
      if (verified?.syncSupported) {
        setCredentialsTarget(verified)
        setAppPassword('')
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Verification failed')
    } finally {
      setVerifySubmitting(false)
    }
  }

  const handleSaveCredentials = async (e) => {
    e.preventDefault()
    if (!credentialsTarget) return
    setSavingCredentials(true)
    try {
      const res = await axios.post(`/api/mailbox/accounts/${credentialsTarget.id}/credentials`, {
        appPassword,
      })
      toast.success(res.data.message || 'Mail access configured')
      setCredentialsTarget(null)
      setAppPassword('')
      await loadAccounts()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save credentials')
    } finally {
      setSavingCredentials(false)
    }
  }

  const handleSyncInbox = async (acc) => {
    setSyncingAccount(acc.id)
    try {
      const res = await axios.post(`/api/mailbox/accounts/${acc.id}/sync`)
      toast.success(res.data.message || 'Inbox synced')
      await loadAccounts()
      if (tab === 'inbox') await loadMailbox()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed')
    } finally {
      setSyncingAccount(null)
    }
  }

  const sendReadyAccounts = useMemo(
    () => mailboxAccounts.filter((a) => a.mailReady),
    [mailboxAccounts]
  )

  const handleDisconnect = async (acc) => {
    setConnecting(acc.id)
    try {
      await axios.post(`/api/mailbox/accounts/${acc.id}/disconnect`)
      toast.success('Disconnected')
      await loadAccounts()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Disconnect failed')
    } finally {
      setConnecting(null)
    }
  }

  const handleAddAccount = async (e) => {
    e.preventDefault()
    setAddingAccount(true)
    try {
      const res = await axios.post('/api/mailbox/accounts', {
        email: newAccountEmail.trim(),
        label: newAccountLabel.trim() || undefined,
      })
      const account = res.data.account || res.data
      toastEmailCodeResult(
        { data: res.data.verify || res.data },
        toast,
        'Verification code sent — check your inbox to confirm this mailbox'
      )
      setShowAddAccount(false)
      setNewAccountEmail('')
      setNewAccountLabel('')
      await loadAccounts()
      openVerifyModal(account)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add account')
    } finally {
      setAddingAccount(false)
    }
  }

  const handleCreateCampaign = async (e) => {
    e.preventDefault()
    setCreatingCampaign(true)
    try {
      const res = await axios.post('/api/email-campaigns', {
        name: campaignForm.name,
        subject: campaignForm.subject,
        body: campaignForm.body,
        audience: [campaignForm.audience],
        tracking: true,
      })
      toast.success('Campaign submitted for approval')
      setShowCampaignModal(false)
      setCampaignForm({ name: '', subject: '', body: '', audience: 'lp' })
      await loadCampaigns()
      setSelectedCampaign(mapCampaign(res.data))
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create campaign')
    } finally {
      setCreatingCampaign(false)
    }
  }

  const updateExtractionPref = (key, value) => {
    setExtractionPrefs((prev) => {
      const next = { ...prev, [key]: value }
      localStorage.setItem(EXTRACTION_PREFS_KEY, JSON.stringify(next))
      return next
    })
    toast.success('Preference saved')
  }

  return (
    <div className="admin-tool-email">
      <div className="admin-tool-topbar">
        <div className="admin-tool-topbar-left">
          <span className="admin-tool-topbar-title">Email</span>
        </div>
        <div className="admin-tool-topbar-right">
          <div className="admin-tool-search">
            <RsIcon name="search" size="sm" aria-hidden />
            <input type="text" placeholder="Search emails..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button type="button" className="admin-tool-icon-btn" title="Refresh" onClick={refresh}>Refresh</button>
        </div>
      </div>

      <div className="admin-tool-tab-nav">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`admin-tool-tab-btn${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === 'inbox' && inboxUnread > 0 ? (
              <span className="admin-tool-tab-badge">{inboxUnread}</span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'inbox' && (
        <div className="admin-tool-tab-panel active">
          <div className="admin-email-inbox">
            <div className="admin-email-folders">
              <button type="button" className="admin-email-compose" onClick={() => openCompose('compose', { accountId: accountFilter || 'gmail' })}>Compose</button>
              <div className="admin-email-folder-section">
                <div className="admin-email-folder-header">All Mailboxes</div>
                {FOLDERS.map(({ id, icon, name, countKey }) => {
                  const count = countKey === 'inbox' ? inboxUnread : countKey === 'drafts' ? draftsCount : 0
                  return (
                  <button
                    key={id}
                    type="button"
                    className={`admin-email-folder-item${!accountFilter && folder === id ? ' active' : ''}`}
                    onClick={() => handleFolderChange(id)}
                  >
                    <span className="admin-email-folder-icon">{resolveIconNode(icon, { size: 'sm' })}</span>
                    <span className="admin-email-folder-name">{name}</span>
                    {count > 0 ? <span className="admin-email-folder-count">{count}</span> : null}
                  </button>
                  )
                })}
              </div>
              {mailboxAccounts.map((acc) => {
                const accountUnread = countFolderEmails(emails, 'inbox', acc.id)
                const isAccountActive = accountFilter === acc.id
                return (
                <div key={acc.id} className={`admin-email-folder-section${isAccountActive ? ' account-active' : ''}`}>
                  <button
                    type="button"
                    className={`admin-email-account-header${isAccountActive ? ' active' : ''}`}
                    onClick={() => handleAccountSelect(acc.id)}
                  >
                    <span className={`admin-email-account-dot ${acc.type}`} />
                    <span className="admin-email-account-email">{acc.email}</span>
                    <span className="admin-email-account-provider">{acc.label}</span>
                  </button>
                  <button
                    type="button"
                    className={`admin-email-folder-item${isAccountActive && folder === 'inbox' ? ' active' : ''}`}
                    onClick={() => handleAccountSelect(acc.id)}
                  >
                    <span className="admin-email-folder-icon"><RsIcon name="inbox" size="sm" /></span>
                    <span className="admin-email-folder-name">Inbox</span>
                    {accountUnread > 0 ? <span className="admin-email-folder-count">{accountUnread}</span> : null}
                  </button>
                </div>
                )
              })}
            </div>

            <div className="admin-email-list-panel">
              <div className="admin-email-list-header">
                <div className="admin-email-list-heading">
                  <span className="admin-email-list-title">{listTitle}</span>
                  <span className="admin-email-list-subtitle">{listSubtitle}</span>
                </div>
                <div className="admin-email-list-actions">
                  <button
                    type="button"
                    onClick={async () => {
                      const visible = filteredEmails.filter((e) => e.unread)
                      setEmails((prev) => prev.map((e) => (visible.some((v) => v.id === e.id) ? { ...e, unread: false } : e)))
                      await Promise.all(visible.map((e) => axios.patch(`/api/mailbox/${e.id}`, { unread: false })))
                    }}
                  >
                    Mark all read
                  </button>
                  <div style={{ position: 'relative' }}>
                    <button type="button" onClick={() => setShowFilterMenu((v) => !v)}>
                      Filter{listFilter !== 'all' ? `: ${LIST_FILTERS.find((f) => f.id === listFilter)?.label}` : ''}
                    </button>
                    {showFilterMenu && (
                      <div className="admin-email-filter-menu">
                        {LIST_FILTERS.map((f) => (
                          <button
                            key={f.id}
                            type="button"
                            className={listFilter === f.id ? 'active' : ''}
                            onClick={() => {
                              setListFilter(f.id)
                              setShowFilterMenu(false)
                            }}
                          >
                            {f.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="admin-email-list">
                {loadingMailbox ? (
                  <div className="admin-email-list-empty">Loading…</div>
                ) : filteredEmails.length === 0 ? (
                  <div className="admin-email-list-empty">No messages in this folder</div>
                ) : filteredEmails.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className={`admin-email-row${selectedEmail?.id === e.id ? ' active' : ''}${e.unread ? ' unread' : ''}`}
                    onClick={() => handleSelectEmail(e)}
                  >
                    <span className={`admin-email-account-indicator ${e.account}`} />
                    <span
                      className={`admin-email-star${e.starred ? ' starred' : ''}`}
                      onClick={(ev) => toggleStar(e, ev)}
                      onKeyDown={(ev) => ev.key === 'Enter' && toggleStar(e, ev)}
                      role="button"
                      tabIndex={0}
                    >
                      ★
                    </span>
                    <div className="admin-email-content-col">
                      <div className="admin-email-top-row">
                        <span className="admin-email-sender">{e.sender}</span>
                        <span className="admin-email-time">{e.time}</span>
                      </div>
                      <div className="admin-email-subject">{e.subject}</div>
                      <div className="admin-email-preview">{e.preview}</div>
                      {e.tags?.length ? (
                        <div className="admin-email-tags">
                          {e.tags.includes('deal') && <span className="admin-email-tag deal">Deal</span>}
                          {e.tags.includes('attachment') && <span className="admin-email-tag attachment"><RsIcon name="files" size="xs" /></span>}
                          {e.tags.includes('important') && <span className="admin-email-tag important">Important</span>}
                          {e.tags.includes('extracted') && <span className="admin-email-tag extracted">Extracted</span>}
                        </div>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="admin-email-detail">
              {selectedEmail ? (
                <>
                  <div className="admin-email-detail-header">
                    <div className="admin-email-detail-subject">{selectedEmail.subject}</div>
                    <div className="admin-email-detail-meta">
                      <div className="admin-email-detail-avatar">{selectedEmail.sender.charAt(0)}</div>
                      <div className="admin-email-detail-sender-info">
                        <div className="admin-email-detail-from">{selectedEmail.sender}</div>
                        <div className="admin-email-detail-from-email">{selectedEmail.email}</div>
                        <div className="admin-email-detail-to">
                          To: {mailboxAccounts.find((a) => a.id === selectedEmail.account)?.email || activeAccount?.email || '—'}
                        </div>
                      </div>
                      <div className="admin-email-detail-date">{selectedEmail.time}</div>
                    </div>
                    <div className="admin-email-detail-actions">
                      {selectedEmail.folder === 'drafts' ? (
                        <button type="button" className="admin-email-action-btn primary" onClick={() => openCompose('compose', buildComposeDraft(selectedEmail))}>Edit Draft</button>
                      ) : (
                        <>
                          <button type="button" className="admin-email-action-btn" onClick={() => openCompose('reply', buildReplyDraft(selectedEmail))}>↩ Reply</button>
                          <button type="button" className="admin-email-action-btn" onClick={() => openCompose('forward', buildForwardDraft(selectedEmail))}>↪ Forward</button>
                          <button type="button" className="admin-email-action-btn primary" onClick={handleArchive}>Archive</button>
                          <button type="button" className="admin-email-action-btn" onClick={handleTrash}>Delete</button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="admin-email-detail-body" dangerouslySetInnerHTML={{ __html: selectedEmail.body }} />
                  {selectedEmail.attachments?.length ? (
                    <div className="admin-email-attachments">
                      <div className="admin-email-attachments-title">Attachments</div>
                      {selectedEmail.attachments.map((a) => (
                        <div key={a.name} className="admin-email-attachment-row">
                          <RsIcon name="fileText" size="sm" />
                          <div>
                            <div>{a.name}</div>
                            <div style={{ fontSize: 11, color: '#909399' }}>{a.size}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {selectedEmail.extraction && (
                    <div className="admin-email-extraction">
                      <div className="admin-email-extraction-header">
                        <span>Content Extraction</span>
                        <span className="admin-email-extraction-badge">Auto</span>
                      </div>
                      <div className="admin-email-extraction-body">
                        {Object.entries(selectedEmail.extraction).map(([k, v]) => (
                          <div key={k} className="admin-email-extraction-item">
                            <span className="admin-email-extraction-label">{k}</span>
                            <span>{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="admin-email-detail-empty">
                  <RsIcon name="email" size="xl" />
                  <span>Select an email to read</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'super' && (
        <div className="admin-tool-tab-panel active">
          <div className="admin-email-super">
            <div className="admin-campaign-panel">
              <div className="admin-campaign-header">
                <span>Campaigns</span>
                <button type="button" className="admin-campaign-new" onClick={() => setShowCampaignModal(true)} disabled={loadingCampaigns}>+ New Campaign</button>
              </div>
              <div className="admin-campaign-list">
                {loadingCampaigns ? (
                  <p style={{ padding: 16, color: '#909399', fontSize: 13 }}>Loading…</p>
                ) : campaigns.length === 0 ? (
                  <p style={{ padding: 16, color: '#909399', fontSize: 13 }}>No campaigns yet</p>
                ) : campaigns.map((c) => {
                  const st = CAMPAIGN_STATUS[c.status] || CAMPAIGN_STATUS.draft
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`admin-campaign-row${selectedCampaign?.id === c.id ? ' active' : ''}`}
                      onClick={() => setSelectedCampaign(c)}
                    >
                      <div className="admin-campaign-name">{c.name}</div>
                      <div className="admin-campaign-meta">
                        <span className={`admin-campaign-status ${st.cls}`}>{st.label}</span>
                        <span>{c.audienceCount || 0} recipients</span>
                      </div>
                      {c.stats && (
                        <div className="admin-campaign-stats">
                          <span>Sent <strong>{c.stats.sent}</strong></span>
                          <span>Opened <strong>{c.stats.opened}</strong></span>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="admin-campaign-detail">
              {selectedCampaign ? (
                <div className="admin-campaign-editor">
                  <div className="admin-campaign-editor-section">
                    <div className="admin-campaign-editor-title">Campaign Name</div>
                    <input className="admin-field-input" readOnly value={selectedCampaign.name} />
                  </div>
                  <div className="admin-campaign-editor-section">
                    <div className="admin-campaign-editor-title">Subject</div>
                    <input className="admin-field-input" readOnly value={selectedCampaign.subject} />
                  </div>
                  <div className="admin-campaign-editor-section">
                    <div className="admin-campaign-editor-title">Audience</div>
                    <input className="admin-field-input" readOnly value={`${selectedCampaign.audience || 'all'} (${selectedCampaign.audienceCount || 0})`} />
                  </div>
                  {selectedCampaign.stats && (
                    <div className="admin-campaign-stats-grid">
                      {[
                        ['Sent', selectedCampaign.stats.sent],
                        ['Delivered', selectedCampaign.stats.delivered],
                        ['Opened', selectedCampaign.stats.opened],
                        ['Clicked', selectedCampaign.stats.clicked],
                      ].map(([label, val]) => (
                        <div key={label} className="admin-campaign-stat-box">
                          <div className="admin-campaign-stat-val">{val}</div>
                          <div className="admin-campaign-stat-lbl">{label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="admin-campaign-send-bar">
                    <span className="admin-campaign-send-info">Status: <strong>{selectedCampaign.status}</strong></span>
                    <div>
                      {isAdmin && selectedCampaign.status === 'pending' && (
                        <>
                          <button type="button" className="admin-btn secondary" onClick={() => handleReject(selectedCampaign.id)}>Reject</button>
                          <button type="button" className="admin-btn primary" onClick={() => handleApprove(selectedCampaign.id)}>Approve & Send</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="admin-email-detail-empty">
                  <RsIcon name="inbox" size="lg" />
                  <span>Select a campaign</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'settings' && (
        <div className="admin-tool-tab-panel active admin-email-settings">
          <div className="admin-email-settings-inner">
            <section className="admin-email-settings-section">
              <h3>Connected Mailboxes</h3>
              <p>Verify your email, then add an app password to sync inbox and send from your address (QQ, Gmail, Outlook, 163, Tencent Exmail).</p>
              {mailboxAccounts.map((acc) => {
                const connected = isConnected(acc)
                return (
                  <div key={acc.id} className="admin-bound-account">
                    <div className={`admin-bound-account-avatar ${acc.type}`}>
                      <RsIcon name={acc.type === 'exchange' ? 'fund' : 'email'} size="md" />
                    </div>
                    <div className="admin-bound-account-info">
                      <div>{acc.email}</div>
                      <div>{acc.label}</div>
                    </div>
                    <span className={`admin-bound-status${connected ? ' connected' : ''}`}>
                      {connected ? (acc.mailReady ? '● Ready' : '● Verified — set up mail access') : '● Disconnected'}
                    </span>
                    {connected ? (
                      <div className="admin-bound-actions">
                        {!acc.mailReady && acc.syncSupported ? (
                          <button
                            type="button"
                            className="admin-btn sm primary"
                            onClick={() => {
                              setCredentialsTarget(acc)
                              setAppPassword('')
                            }}
                          >
                            Set up mail access
                          </button>
                        ) : null}
                        {acc.mailReady ? (
                          <button
                            type="button"
                            className="admin-btn sm secondary"
                            disabled={syncingAccount === acc.id}
                            onClick={() => handleSyncInbox(acc)}
                          >
                            {syncingAccount === acc.id ? 'Syncing…' : 'Sync inbox'}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="admin-btn sm secondary"
                          disabled={connecting === acc.id}
                          onClick={() => handleDisconnect(acc)}
                        >
                          Disconnect
                        </button>
                      </div>
                    ) : (
                      <div className="admin-bound-actions">
                        {acc.provider && (
                          <button
                            type="button"
                            className="admin-btn sm secondary"
                            disabled={connecting === acc.id}
                            onClick={() => handleConnect(acc)}
                          >
                            {connecting === acc.id ? '…' : 'OAuth'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="admin-btn sm primary"
                          onClick={() => {
                            openVerifyModal(acc)
                            handleSendVerifyCode(acc)
                          }}
                        >
                          Verify email
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
              <button type="button" className="admin-bound-add" onClick={() => setShowAddAccount(true)}>+ Add Email Account</button>
            </section>
            <section className="admin-email-settings-section">
              <h3>Content Extraction</h3>
              <p>Automatically extract deal info, meeting details, and contacts from emails.</p>
              {[
                { key: 'dealRefs', label: 'Auto-extract deal references' },
                { key: 'meetings', label: 'Auto-extract meeting invites' },
                { key: 'confidential', label: 'Flag confidential attachments' },
              ].map(({ key, label }) => (
                <div key={key} className="admin-settings-toggle-row">
                  <div className="admin-settings-toggle-left">
                    <div><div className="admin-settings-toggle-name">{label}</div></div>
                  </div>
                  <label className="admin-toggle">
                    <input
                      type="checkbox"
                      checked={!!extractionPrefs[key]}
                      onChange={(e) => updateExtractionPref(key, e.target.checked)}
                    />
                    <span className="admin-toggle-slider" />
                  </label>
                </div>
              ))}
            </section>
          </div>
        </div>
      )}
      <EmailComposeModal
        open={composeOpen}
        mode={composeMode}
        initial={composeInitial}
        accounts={sendReadyAccounts.length ? sendReadyAccounts : mailboxAccounts.filter((a) => a.connected)}
        sending={sendingMail}
        onClose={() => setComposeOpen(false)}
        onSend={handleComposeSend}
        onSaveDraft={handleSaveDraft}
      />

      {showAddAccount && (
        <div className="admin-email-compose-overlay" onClick={() => setShowAddAccount(false)} role="presentation">
          <div className="admin-email-compose-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="admin-email-compose-header">
              <span>Add Email Account</span>
              <button type="button" className="admin-email-compose-close" onClick={() => setShowAddAccount(false)} aria-label="Close">×</button>
            </div>
            <form className="admin-email-compose-fields" onSubmit={handleAddAccount}>
              <label className="admin-email-compose-row">
                <span>Email</span>
                <input type="email" required value={newAccountEmail} onChange={(e) => setNewAccountEmail(e.target.value)} placeholder="you@company.com" />
              </label>
              <label className="admin-email-compose-row">
                <span>Label</span>
                <input type="text" value={newAccountLabel} onChange={(e) => setNewAccountLabel(e.target.value)} placeholder="Optional display name" />
              </label>
              <div className="admin-email-compose-actions">
                <button type="button" className="admin-btn secondary" onClick={() => setShowAddAccount(false)}>Cancel</button>
                <button type="submit" className="admin-btn primary" disabled={addingAccount}>{addingAccount ? 'Adding…' : 'Add Account'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {credentialsTarget && (
        <div className="admin-email-compose-overlay" onClick={() => setCredentialsTarget(null)} role="presentation">
          <div className="admin-email-compose-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="admin-email-compose-header">
              <span>Mail access — {credentialsTarget.email}</span>
              <button type="button" className="admin-email-compose-close" onClick={() => setCredentialsTarget(null)} aria-label="Close">×</button>
            </div>
            <form className="admin-email-compose-fields" onSubmit={handleSaveCredentials}>
              <p className="admin-email-verify-hint">
                Enter the <strong>app password / authorization code</strong> from your email provider (not your login password).
                For QQ Mail: Settings → Account → Generate authorization code. Enable IMAP/SMTP in mailbox settings first.
              </p>
              <label className="admin-email-compose-row">
                <span>App password</span>
                <input
                  type="password"
                  required
                  autoComplete="off"
                  value={appPassword}
                  onChange={(e) => setAppPassword(e.target.value)}
                  placeholder="16-character authorization code"
                />
              </label>
              <div className="admin-email-compose-actions">
                <button type="button" className="admin-btn secondary" onClick={() => setCredentialsTarget(null)}>Later</button>
                <button type="submit" className="admin-btn primary" disabled={savingCredentials}>
                  {savingCredentials ? 'Saving…' : 'Save & enable send/sync'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {verifyTarget && (
        <div className="admin-email-compose-overlay" onClick={() => setVerifyTarget(null)} role="presentation">
          <div className="admin-email-compose-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="admin-email-compose-header">
              <span>Verify mailbox — {verifyTarget.email}</span>
              <button type="button" className="admin-email-compose-close" onClick={() => setVerifyTarget(null)} aria-label="Close">×</button>
            </div>
            <form className="admin-email-compose-fields" onSubmit={handleVerifySubmit}>
              <p className="admin-email-verify-hint">
                Enter the 6-digit code sent to <strong>{verifyTarget.email}</strong> to confirm you own this mailbox.
              </p>
              <label className="admin-email-compose-row">
                <span>Verification code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                />
              </label>
              <div className="admin-email-compose-actions">
                <button
                  type="button"
                  className="admin-btn secondary"
                  disabled={verifySending}
                  onClick={() => handleSendVerifyCode()}
                >
                  {verifySending ? 'Sending…' : 'Resend code'}
                </button>
                <button type="submit" className="admin-btn primary" disabled={verifySubmitting || verifyCode.length < 6}>
                  {verifySubmitting ? 'Verifying…' : 'Verify & connect'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCampaignModal && (
        <div className="admin-email-compose-overlay" onClick={() => setShowCampaignModal(false)} role="presentation">
          <div className="admin-email-compose-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="admin-email-compose-header">
              <span>New Super Email Campaign</span>
              <button type="button" className="admin-email-compose-close" onClick={() => setShowCampaignModal(false)} aria-label="Close">×</button>
            </div>
            <form className="admin-email-compose-fields" onSubmit={handleCreateCampaign}>
              <label className="admin-email-compose-row">
                <span>Name</span>
                <input required value={campaignForm.name} onChange={(e) => setCampaignForm((p) => ({ ...p, name: e.target.value }))} />
              </label>
              <label className="admin-email-compose-row">
                <span>Subject</span>
                <input required value={campaignForm.subject} onChange={(e) => setCampaignForm((p) => ({ ...p, subject: e.target.value }))} />
              </label>
              <label className="admin-email-compose-row">
                <span>Audience</span>
                <select value={campaignForm.audience} onChange={(e) => setCampaignForm((p) => ({ ...p, audience: e.target.value }))}>
                  {AUDIENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="admin-email-compose-row" style={{ alignItems: 'flex-start' }}>
                <span>Body</span>
                <textarea required rows={6} value={campaignForm.body} onChange={(e) => setCampaignForm((p) => ({ ...p, body: e.target.value }))} />
              </label>
              <div className="admin-email-compose-actions">
                <button type="button" className="admin-btn secondary" onClick={() => setShowCampaignModal(false)}>Cancel</button>
                <button type="submit" className="admin-btn primary" disabled={creatingCampaign}>{creatingCampaign ? 'Submitting…' : 'Submit for Approval'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
